import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import BaseDialog from "../utils/dialog/BaseDialog";
import {
  formatPdfPageNumber,
  PDF_PAGE_NUMBER_STYLE_OPTIONS,
} from "./utils/pdfFooterOptions";

const FOOTER_FIELDS = [
  { key: "leftText", label: "Left", position: "left" },
  { key: "centerText", label: "Centre", position: "center" },
  { key: "rightText", label: "Right", position: "right" },
];

const PAGE_NUMBER_POSITIONS = [
  { value: "left", label: "Left", icon: <FormatAlignLeftIcon /> },
  { value: "center", label: "Centre", icon: <FormatAlignCenterIcon /> },
  { value: "right", label: "Right", icon: <FormatAlignRightIcon /> },
];

const getFooterPreviewText = (footer, field) => {
  const parts = footer.enabled ? [footer[field.key]] : [];

  if (
    footer.pageNumber.enabled &&
    footer.pageNumber.position === field.position
  ) {
    parts.push(
      formatPdfPageNumber({
        pageNumber: 1,
        pageCount: 10,
        style: footer.pageNumber.style,
      }),
    );
  }

  return parts.filter(Boolean).join(" | ") || "[No text]";
};

const PdfGenerationOptionsDialog = ({
  open,
  busy,
  pdfType,
  storePdf,
  footer,
  onClose,
  onContinue,
  onStorePdfChange,
  onFooterChange,
}) => (
  <BaseDialog
    open={open}
    onClose={onClose}
    closeDisabled={busy}
    maxWidth="md"
    title="Prepare Full Tax Computation PDF"
    subtitle="Set your preferences and store your PDF version before generating the PDF file."
    icon={<PictureAsPdfOutlinedIcon fontSize="small" />}
    iconColor="error.main"
    paperSx={{
      maxHeight: "calc(100dvh - 32px)",
      overflow: "hidden",
    }}
    contentSx={{ p: { xs: 2, sm: 3 } }}
    actions={
      <>
        <Button onClick={onClose} disabled={busy} variant="outlined">
          Cancel
        </Button>
        <Button onClick={onContinue} disabled={busy} variant="contained">
          Continue
        </Button>
      </>
    }
    actionsSx={{
      flexWrap: "wrap",
      gap: 1,
      "& .MuiButton-root": {
        flex: { xs: "1 1 100%", sm: "0 0 auto" },
        m: "0 !important",
      },
    }}
  >
    <Stack divider={<Divider flexItem />} spacing={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <PictureAsPdfOutlinedIcon color="error" fontSize="small" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {pdfType === "draft" ? "Draft PDF" : "Final PDF"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            All saved statements and schedules will be combined into one file.
          </Typography>
        </Box>
      </Stack>

      <Box>
        <FormControlLabel
          control={
            <Switch
              disabled={busy}
              checked={storePdf}
              onChange={(event) => onStorePdfChange(event.target.checked)}
            />
          }
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <FolderCopyOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Store PDF version
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  Keep a copy in Stored PDF Versions after generation.
                </Typography>
              </Box>
            </Stack>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          Page footer
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          Configure footer text and page numbering for the combined PDF.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              disabled={busy}
              checked={footer.enabled}
              onChange={(event) =>
                onFooterChange({
                  ...footer,
                  enabled: event.target.checked,
                })
              }
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={700}>
                Add a footer
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                Add optional text to the left, centre, or right of every page.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />

        <Collapse in={footer.enabled} unmountOnExit>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              mt: 2,
            }}
          >
            {FOOTER_FIELDS.map((field) => (
              <TextField
                key={field.key}
                fullWidth
                size="small"
                disabled={busy}
                label={`${field.label} footer`}
                value={footer[field.key]}
                onChange={(event) =>
                  onFooterChange({
                    ...footer,
                    [field.key]: event.target.value,
                  })
                }
                inputProps={{ maxLength: 80 }}
                helperText={`${footer[field.key].length}/80`}
                FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
              />
            ))}
          </Box>
        </Collapse>

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            mt: 2,
            pt: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                disabled={busy}
                checked={footer.pageNumber.enabled}
                onChange={(event) =>
                  onFooterChange({
                    ...footer,
                    pageNumber: {
                      ...footer.pageNumber,
                      enabled: event.target.checked,
                    },
                  })
                }
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Add page numbers
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  Display the current and total page count on every included
                  page.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
          />

          <Collapse in={footer.pageNumber.enabled} unmountOnExit>
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                mt: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="label"
                  htmlFor="pdf-page-number-style"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75 }}
                >
                  Page number style
                </Typography>
                <TextField
                  id="pdf-page-number-style"
                  select
                  fullWidth
                  size="small"
                  disabled={busy}
                  value={footer.pageNumber.style}
                  onChange={(event) =>
                    onFooterChange({
                      ...footer,
                      pageNumber: {
                        ...footer.pageNumber,
                        style: event.target.value,
                      },
                    })
                  }
                >
                  {PDF_PAGE_NUMBER_STYLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ width: "100%" }}
                      >
                        <Box component="span">{option.label}</Box>
                        <Typography variant="caption" color="text.secondary">
                          {option.example}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75 }}
                >
                  Page number position
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  disabled={busy}
                  value={footer.pageNumber.position}
                  onChange={(_, position) => {
                    if (!position) return;
                    onFooterChange({
                      ...footer,
                      pageNumber: { ...footer.pageNumber, position },
                    });
                  }}
                  aria-label="Page number position"
                >
                  {PAGE_NUMBER_POSITIONS.map((position) => (
                    <ToggleButton
                      key={position.value}
                      value={position.value}
                      aria-label={`${position.label} page number`}
                      sx={{ gap: 0.75 }}
                    >
                      {position.icon}
                      <Box component="span">{position.label}</Box>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Collapse
          in={footer.enabled || footer.pageNumber.enabled}
          unmountOnExit
        >
          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 2,
              pt: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  disabled={busy}
                  checked={footer.includeContentPage}
                  onChange={(event) =>
                    onFooterChange({
                      ...footer,
                      includeContentPage: event.target.checked,
                    })
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Include Content Page
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    Apply the selected footer options to the opening Content
                    Page and include it in page numbering.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />
          </Box>

          <Box
            aria-label="Footer preview"
            sx={{
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              display: "grid",
              gap: 1,
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              mt: 1.5,
              px: 1.5,
              py: 1,
            }}
          >
            {FOOTER_FIELDS.map((field, index) => (
              <Typography
                key={field.key}
                variant="caption"
                color={
                  getFooterPreviewText(footer, field) !== "[No text]"
                    ? "text.secondary"
                    : "text.disabled"
                }
                noWrap
                sx={{ textAlign: ["left", "center", "right"][index] }}
              >
                {getFooterPreviewText(footer, field)}
              </Typography>
            ))}
          </Box>
          {!footer.includeContentPage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75 }}
            >
              Content Page footer is excluded. Page numbering starts from the
              following page.
            </Typography>
          )}
        </Collapse>
      </Box>
    </Stack>
  </BaseDialog>
);

export default PdfGenerationOptionsDialog;
